import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Users, GraduationCap, MessagesSquare, Sparkles, Download } from "lucide-react";
import Reveal from "@/components/Reveal";
import ExperienceWishlist from "@/components/ExperienceWishlist";
import useSEO from "@/hooks/useSEO";

const EXPERIENCES = [
  {
    icon: Users,
    tag: "Family Experiences",
    body: "Creating meaningful opportunities for youth and families to connect, have fun, and make positive memories together.",
  },
  {
    icon: GraduationCap,
    tag: "Career & Education Exposure",
    body: "Introducing youth to colleges, careers, skilled trades, businesses, entrepreneurs, and professionals.",
  },
  {
    icon: MessagesSquare,
    tag: "Mentorship & Community Connections",
    body: "Connecting young people with positive role models and community partners who encourage growth and possibility.",
  },
  {
    icon: Sparkles,
    tag: "New Experiences",
    body: "Providing access to recreational, cultural, educational, and community experiences youth and families may not otherwise enjoy.",
  },
];

const WAYS = [
  "Experiences", "Mentorship", "Admission passes", "Educational opportunities",
  "Transportation", "Meals", "Supplies", "Sponsorships", "Financial support",
];

// NEXT MOVE wordmark — bold "Next", flowing italic "Move", teal forward arrow, ™
function NextMoveMark({ className = "", arrow = 34 }) {
  return (
    <span className={`inline-flex items-center font-sans leading-none ${className}`}>
      <span className="font-extrabold tracking-tight">Next</span>
      <span className="italic font-light ml-[0.35em] mr-[0.15em]">Move</span>
      <svg width={arrow} height={arrow * 0.6} viewBox="0 0 50 30" fill="none" className="text-cyan-400 -ml-1 -mt-[0.25em]" aria-hidden="true">
        <path d="M2 22 C 14 22, 30 18, 44 6" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
        <path d="M33 5 L46 4 L44 17" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
      <sup className="text-[0.32em] font-normal ml-[0.1em] -translate-y-[0.2em]">™</sup>
    </span>
  );
}

export default function NextMove() {
  useSEO({
    title: "Next Move™ — Youth & Family Initiative | The Tandem T Foundation",
    description: "Next Move™ is Tandem T Foundation's youth and family initiative — turning 'What's next?' into opportunity, connection, and forward movement through experiences, mentorship, and career & educational exposure.",
    path: "/foundation/next-move",
  });

  return (
    <div data-testid="page-next-move" className="bg-gradient-to-b from-sky-50 via-white to-emerald-50/60 text-slate-800">
      {/* HERO */}
      <section className="relative min-h-[82vh] flex items-center overflow-hidden" data-testid="nm-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-600 to-emerald-500">
          <div className="absolute -top-24 -right-16 w-[38rem] h-[38rem] rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-32 -left-20 w-[34rem] h-[34rem] rounded-full bg-emerald-300/20 blur-3xl" aria-hidden="true" />
        </div>
        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 md:px-10 pt-28 pb-20">
          <Reveal>
            <p className="overline text-white/80">The Tandem T Foundation</p>
          </Reveal>
          <Reveal delay={0.08}>
            <NextMoveMark className="text-white text-6xl sm:text-7xl lg:text-8xl mt-5" arrow={54} />
          </Reveal>
          <Reveal delay={0.16}>
            <p className="font-serif italic text-white/95 text-2xl md:text-4xl font-light leading-snug mt-8 max-w-3xl">
              When life asks, &ldquo;What&rsquo;s next?&rdquo; we help make the Next Move possible.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/foundation/new-beginnings#partner-form-section" data-testid="nm-partner"
                className="group inline-flex items-center gap-3 overline px-8 py-5 bg-white text-cyan-700 hover:bg-cyan-50 transition-colors rounded-full">
                Become a Partner <ArrowUpRight size={16} className="hover-arrow" />
              </Link>
              <Link to="/foundation/donate" data-testid="nm-donate"
                className="group inline-flex items-center gap-3 overline px-8 py-5 border border-white/60 text-white hover:bg-white/10 transition-colors rounded-full">
                Support Next Move <ArrowUpRight size={16} className="hover-arrow" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT'S NEXT — intro */}
      <section className="py-20 md:py-28 px-6 md:px-10" data-testid="nm-intro">
        <div className="max-w-[1000px] mx-auto text-center">
          <Reveal>
            <p className="overline text-cyan-600">After hardship, change, or an unexpected turn</p>
            <h2 className="font-serif font-light text-slate-900 text-4xl md:text-6xl mt-5 leading-[1.08] tracking-tight">
              One of the hardest questions can be:{" "}
              <span className="italic text-cyan-600">&ldquo;What&rsquo;s next?&rdquo;</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-600 font-light leading-relaxed mt-8 max-w-2xl mx-auto text-lg">
              <span className="font-medium text-slate-900"><NextMoveMark className="text-xl" arrow={22} /></span> is
              Tandem T Foundation&rsquo;s answer — our youth and family initiative designed to turn that question
              into <strong className="font-semibold text-slate-800">opportunity, connection, and forward movement.</strong>{" "}
              Through meaningful experiences, mentorship, career and educational exposure, recreation, and
              family-centered activities, we help young people and families discover what can come next.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CREATING EXPERIENCES */}
      <section className="py-16 md:py-24 px-6 md:px-10" data-testid="nm-creating">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-7">
            <p className="overline text-cyan-600">Creating Experiences. Opening Possibilities.</p>
            <h2 className="font-serif font-light text-slate-900 text-3xl md:text-5xl mt-4 leading-tight tracking-tight">
              For one young person, the next move may be casting a fishing line{" "}
              <span className="italic text-cyan-600">for the first time.</span>
            </h2>
            <p className="text-slate-600 font-light leading-relaxed mt-6 text-lg">
              For another, it may be stepping onto a college campus, discovering a career they never knew existed,
              meeting a mentor who believes in them, or simply sharing a new experience with their family.
              Next Move&trade; creates opportunities to explore, learn, connect, build confidence, and imagine a
              future beyond the circumstances of today.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-5">
            <div className="relative rounded-[1.75rem] bg-gradient-to-br from-cyan-600 to-emerald-500 text-white p-10 md:p-12 overflow-hidden">
              <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full bg-white/10 blur-2xl" aria-hidden="true" />
              <p className="relative overline text-white/80">Every experience is a door</p>
              <p className="relative font-serif italic font-light text-3xl md:text-4xl leading-snug mt-4">
                Explore. Learn. Connect. Build confidence. Imagine what&rsquo;s next.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EXPERIENCES GRID */}
      <section className="py-16 md:py-24 px-6 md:px-10" data-testid="nm-experiences">
        <div className="max-w-[1300px] mx-auto">
          <Reveal>
            <p className="overline text-cyan-600 text-center">Our Experiences Include</p>
            <h2 className="font-serif font-light text-slate-900 text-4xl md:text-5xl mt-4 leading-tight tracking-tight text-center">
              Four ways we open <span className="italic text-cyan-600">what&rsquo;s possible.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {EXPERIENCES.map((e, i) => {
              const Icon = e.icon;
              return (
                <Reveal key={e.tag} delay={(i % 4) * 0.06}>
                  <div className="bg-white rounded-[1.5rem] shadow-sm ring-1 ring-slate-200/70 p-8 h-full flex flex-col hover:shadow-lg transition-shadow" data-testid={`nm-exp-${i}`}>
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-600 to-emerald-500 flex items-center justify-center text-white">
                      <Icon size={24} />
                    </div>
                    <h3 className="font-serif font-light text-slate-900 text-2xl mt-6 leading-snug">{e.tag}</h3>
                    <p className="text-slate-600 font-light leading-relaxed mt-3 text-[15px]">{e.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* EXPERIENCE WISHLIST — pledge a specific experience */}
      <ExperienceWishlist />

      {/* CHANGE EVERYTHING */}
      <section className="py-20 md:py-28 px-6 md:px-10" data-testid="nm-change">
        <div className="max-w-[1000px] mx-auto text-center">
          <Reveal>
            <h2 className="font-serif font-light text-slate-900 text-4xl md:text-6xl leading-[1.08] tracking-tight">
              Because the next move can <span className="italic text-cyan-600">change everything.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-600 font-light leading-relaxed mt-8 max-w-2xl mx-auto text-lg">
              We don&rsquo;t expect young people or families facing difficult seasons to have their entire future
              figured out. Sometimes they simply need an opportunity, an experience, a connection, or someone
              willing to remind them that there is still something ahead.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 rounded-[1.25rem] overflow-hidden ring-1 ring-slate-200">
              {[
                { k: "Every Person", v: "Has Value." },
                { k: "Every Story", v: "Matters." },
                { k: "Every Future", v: "Deserves Hope." },
              ].map((t) => (
                <div key={t.k} className="bg-white p-8">
                  <p className="overline text-cyan-600">{t.k}</p>
                  <p className="font-serif font-light text-slate-900 text-2xl md:text-3xl mt-3 italic">{t.v}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-500 font-light mt-6 text-sm max-w-xl mx-auto">
              &hellip; and the chance for a new beginning.
            </p>
            <p className="font-serif font-light text-slate-900 text-2xl md:text-3xl mt-8">
              Every experience carries a simple message:{" "}
              <span className="italic text-cyan-600">You Matter.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* PARTNERSHIP CTA */}
      <section className="py-20 md:py-28 px-6 md:px-10" data-testid="nm-partnership">
        <div className="max-w-[1100px] mx-auto">
          <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-cyan-600 to-emerald-500 p-10 md:p-16 text-white">
            <Reveal>
              <p className="overline text-white/80">Partnership Makes the Next Move Possible</p>
              <h2 className="font-serif font-light text-4xl md:text-6xl mt-4 leading-[1.05] tracking-tight">
                Turn &ldquo;What&rsquo;s next?&rdquo; into{" "}
                <span className="italic">&ldquo;Here&rsquo;s your Next Move.&rdquo;</span>
              </h2>
              <p className="text-white/90 font-light leading-relaxed mt-6 max-w-2xl text-lg">
                Businesses, organizations, professionals, and community members can help provide the experiences
                that change a young person&rsquo;s story.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 flex flex-wrap gap-2">
                {WAYS.map((w) => (
                  <span key={w} className="overline text-[10px] px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm">{w}</span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/foundation/new-beginnings#partner-form-section" data-testid="nm-cta-partner"
                  className="group inline-flex items-center gap-3 overline px-8 py-5 bg-white text-cyan-700 hover:bg-cyan-50 transition-colors rounded-full">
                  Become a Partner <ArrowUpRight size={16} className="hover-arrow" />
                </Link>
                <Link to="/foundation/donate" data-testid="nm-cta-donate"
                  className="group inline-flex items-center gap-3 overline px-8 py-5 border border-white/60 text-white hover:bg-white/10 transition-colors rounded-full">
                  Give to Next Move <ArrowUpRight size={16} className="hover-arrow" />
                </Link>
                <a href="/flyer/Tandem_T_Next_Move_Flyer.pdf" target="_blank" rel="noopener noreferrer" data-testid="nm-flyer-download"
                  className="group inline-flex items-center gap-3 overline px-8 py-5 border border-white/60 text-white hover:bg-white/10 transition-colors rounded-full">
                  <Download size={15} /> Download the Flyer
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-14 text-center">
              <Link to="/foundation" data-testid="nm-back-link" className="group inline-flex items-center gap-3 overline text-slate-500 hover:text-slate-900 transition-colors">
                <ArrowLeft size={15} /> Back to the Foundation
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
